import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';

import { UserManager, UnitOfWork, StateMap, SearchService } from '../../services/common';
import { UtilFns, LocationFns } from '../../utils/common';
import { PageState, SortColumn, ColumnSorter } from '../../controls/common';
import { PaginationService, IPaginationInstance } from '../../controls/ng2-pagination/ng2-pagination';

import { ROUTES } from '../routes';
import {Material, AssessmentCategory, RIFMDownloadableFile} from '../../entities/EntityModels';
import {Observable, Subject} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';

import * as _ from 'lodash';

export class BasicFileInformation {
    FileName: string;
    SubFolder: string;
}

@Component({
    templateUrl: './safety-assessments.html',
    providers: [PaginationService]
})
export class SafetyAssessmentsComponent implements OnInit {

    _materials: Material[];
    _pagination: IPaginationInstance;
    _colSorter: ColumnSorter;
    _countSafetyAssessments = {drafts: 0, reports: 0, matWith: 0};
    _assessmentCategories: AssessmentCategory[] = [];
    _documentManagementUrl: string;
    _isLoading = false;
    _validationMessage: string;
    _validRequest = true;

    _pageState = new PageState('Safety Assessments');

    constructor(public _uow: UnitOfWork, public _stateMap: StateMap, public _searchService: SearchService,
                public _userManager: UserManager, public _location: Location, public _router: Router, private _httpClient: HttpClient) {

    }

    ngOnInit() {

        this._pagination = this._pageState.getPaginationInfo();

        if (!this._userManager.currentUser.isInRole('member') && this._userManager.currentUser.isInRole('femamember')) {
            this._validRequest = false;
            return false;
        }

        // default to publish date descending
        this._colSorter = new ColumnSorter(this, {
                'Publish Date': (m: Material) => ((m as any)['assessmentReportDate'] || new Date(0)).toISOString(),
                'CAS No.': (m: Material) => m.realCASNumber,
                'Synonym': (m: Material) => m.principalSynonymText()
            },
            new SortColumn('Publish Date', false),
            (sortColumn) => this._pageState.sortColumn = sortColumn);

        // setTimeout needed because of Angular RC5 bug where Location is not set correctly until
        // after timeout resolves.
        setTimeout(() => {
            const isReturning = LocationFns.updatePageState(this._location, this._stateMap, this._pageState, (state: PageState) => {
                this._colSorter.sortColumn = state.sortColumn;
            });
            this.search(isReturning);
        }, 0);

        this._uow.fetch('Materials/CountSafetyAssessments', {}).then(ar => {
            const r = ar[0];
            this._countSafetyAssessments.drafts = r.drafts;
            this._countSafetyAssessments.reports = r.reports;
            this._countSafetyAssessments.matWith = r.matWith;
        });

        this._uow.assessmentCategoryRepository.all().then(r => {
            for (const ac of r) {
                this._assessmentCategories[ac.assessmentCategoryId] = ac;
            }
        });

        // needed for file downloads
        this._documentManagementUrl = this._stateMap.documentManagementBaseUrl  + '/RIFMDownloadableFile';

    }

    sortWith(colSorter: ColumnSorter) {
        colSorter.sort(this._materials);
    }

    selectSafetyAssessment(m: Material) {
        this.downloadSAFile(m.assessmentReportName);
    }

    selectCAS(m: Material) {
        if (this.isPermittedRole()) {
            this._router.navigate(UtilFns.asRouterParts(ROUTES.Material, m.materialId, ROUTES.Material.childRoutes.SafetyAssessment));
        } else {
            this._router.navigate(UtilFns.asRouterParts(ROUTES.Material, m.materialId));
        }
    }

    search(isReturning: boolean): Promise<Material[]> {
        this._materials = null;
        setTimeout(() => this._pageState.isLoading = true, 0);
        const sortColumn = this._colSorter.sortColumn;

        return this._uow.fetchTyped('fetchSAMaterialsPage', Material, {})
            .then(r => {
                if (!this.isPermittedRole()) {
                    r = r.filter(m => m.assessmentReportName != null && m.assessmentReportName.trim() != '');
                }

                const addlmap = {} as any;
                r.forEach(m => {
                    (m as any)['assessmentReportDate'] = m.assessmentReportNameModified && m.assessmentReportNameModified;
                    const addlMaterials = m.safetyAssessmentMaterials.map(sam => sam.additionalMaterial);
                    addlMaterials.forEach(a => addlmap[a.cASNumber] = m.assessmentReportName);
                    (m as any)['addlMaterials'] = addlMaterials;
                });

                // filter materials that are listed as additional in the SA, issue #550
                const mats: Material[] = [];
                let materialCount = 0;
                r.forEach(m => {
                    // if this material is not listed as an 'additional material' for the sa, then put it in the list
                    if (!(addlmap[m.cASNumber] == m.assessmentReportName)) {
                        mats.push(m);
                        materialCount++;
                        materialCount += m.safetyAssessmentMaterials.length;
                    }
                });
                this._materials = mats;

                this._pagination.currentPage = this._pageState.pageNumber;
                this._pagination.totalItems = this._pageState.updateEstimate(r.length);
                this._pageState.isLoaded = r;
                if (isReturning) {
                    this._colSorter.sort(this._materials);
                }

                const sa = this._countSafetyAssessments;
                // this._pageState.loadStatusMsg = "There are " + sa.reports + " Safety Assessments covering " + sa.matWith + " materials.";
                this._pageState.loadStatusMsg = 'There are ' + sa.reports + ' Safety Assessments covering ' + materialCount + ' materials.';

                return r;
            });

    }

    isStaff() {
        return this._stateMap.currentUser.isStaff;
    }

    isPermittedRole() {
        return this._userManager.currentUser.isStaff || this._userManager.currentUser.isAgentReadOnly;
    }

    getAssessmentCategories(e: Material) {
        return e.materialAssessmentCategories.map(ac => {
            return this._assessmentCategories[ac.assessmentCategoryId].name;
        }).join(', ');
    }

    getDownloadFileURL(fileName: string): Observable<any> {
        const url = this._documentManagementUrl + '/PostFileBySubFolderAndName';

        const fileInfo = new BasicFileInformation;

        fileInfo.FileName   = fileName;
        fileInfo.SubFolder  = 'SafetyAssessments';

        return this._httpClient.post(url, fileInfo, {responseType: 'blob' as 'json'});
    }

    public downloadSAFile(fileName: string) {
        this._isLoading = true;
        this._validationMessage = '';
        this.getDownloadFileURL(fileName)
            .subscribe(
                (response: Blob) => {
                    const data = response;

                    const file = new Blob([data], { type: 'application/pdf' });
                    const fileURL = URL.createObjectURL(file);

                    // window.open(fileURL);
                    const a         = document.createElement('a');
                    a.href        = fileURL;
                    a.target      = '_blank';
                    a.download    = fileName;
                    document.body.appendChild(a);
                    a.click();

                    a.remove();

                    window.URL.revokeObjectURL(fileURL);

                    this._validationMessage = '';

                },
                (error) => {
                    console.error('Request failed with error: ' + error.message);
                    this._validationMessage = error.message;
                    this._isLoading = false;
                },
                () => {
                    this._isLoading = false;
                });
    }
}
